import { digObject } from '@domain-group/javascript-utils'

import {
  Box,
  DashboardModule,
  FileInput,
  Form,
  Image,
  Link,
  LoadingBubbles,
} from '@domain-group/suit-theme'

import { confirmDelete } from '@functions/entity'
import handleCallbackAction from '@functions/handleCallbackAction'

import useAttachment from '@hooks/useAttachment'
import useAttachments from '@hooks/useAttachments'

interface WatermarkImageModuleProps {
  brandId: number,
}

const WatermarkImageModule = (props: WatermarkImageModuleProps) => {
  const { brandId } = props

  const attachmentsPayload = useAttachments({
    filters: {
      attachable_id: brandId,
      attachable_type: 'Brand',
      attachment_type: 'watermark_image',
    },
    performHttpRequests: !!brandId,
  })
  const { filteredAttachments } = attachmentsPayload

  const watermarkImage = filteredAttachments[0] || {}

  const watermarkImageUrl = digObject(watermarkImage, 'links.original_url')

  const useAttachmentPayload = useAttachment(watermarkImage)
  const {
    attachment: {
      id: attachmentId,
    },
    callbacks: {
      createAttachment: createFn,
      deleteAttachment: deleteFn,
    },
    creating,
  } = useAttachmentPayload

  interface UpdateWatermarkImageParams {
    fileData: string,
    fileObject: {
      type: string,
      name: string,
    },
  }

  const updateWatermarkImage = (params: UpdateWatermarkImageParams) => {
    const { fileData, fileObject: { name, type } } = params

    if (!fileData) return

    const updateWatermarkImagePayload = {
      callbacks: {
        action: createFn,
      },
      entityParams: {
        attachable_id: brandId,
        attachable_type: 'Brand',
        attachment_type: 'watermark_image',
        file: fileData,
        file_content_type: type,
        file_file_name: name,
      },
      requestOptions: {
        include_attachment_file: true,
      },
      toastText: 'Watermark Image Updated',
    }

    handleCallbackAction(updateWatermarkImagePayload)
  }

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
    },
    options: {
      entityParams: { id: attachmentId },
      isBulkDelete: false,
    },
    strings: {
      confirmString: 'Are you sure? This will remove the watermark for all assigned agencies.',
      entityName: 'Watermark',
    },
  }

  return (
    <DashboardModule
      headerRight={watermarkImageUrl
        && (
          <Link
            onClick={() => confirmDelete(confirmDeletePayload)}
            textProps={{ fontSize: 'small' }}
          >
            Delete
          </Link>
        )}
      title="Watermark"
    >
      <Box flexDirection="column" padding="large">
        {watermarkImageUrl && (
          <Form.Field
            fieldHolderProps={{ alignItems: 'center' }}
            label="Current Watermark"
          >
            <Image
              backgroundSize="contain"
              boxProps={{ variant: 'whiteGrey' }}
              placeholderUrl=""
              height={240}
              url={watermarkImageUrl}
            />
          </Form.Field>
        )}

        {!watermarkImageUrl && (
          <Form.Field label="*Upload Watermark">
            {creating
              ? <LoadingBubbles color="blue" />
              : (
                <FileInput
                  input={{
                    onChange: (
                      fileData: string,
                      fileObject: {
                        type: string,
                        name: string,
                      },
                    ) => updateWatermarkImage({
                      fileData, fileObject,
                    }),
                    accept: 'image/*',
                  }}
                />
              )}
          </Form.Field>
        )}
      </Box>
    </DashboardModule>
  )
}

export default WatermarkImageModule
